import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Chip,
  LinearProgress,
  Button,
  Stack,
} from '@mui/material';
import {
  SignalCellularAlt,
  GpsFixed,
  Speed,
  Terrain,
  Timeline,
  Settings,
  Route,
  Dashboard,
  AccessTime,
} from '@mui/icons-material';
import LiveGPSMap from '../../components/LiveGPSMap/LiveGPSMap';
import AssignmentPanel from '../../components/AssignmentPanel/AssignmentPanel';
import MetricsDashboard from '../../components/MetricsDashboard/MetricsDashboard';
import ControlPanel from '../../components/ControlPanel/ControlPanel';
import SystemHealth from '../../components/SystemHealth/SystemHealth';
import RouteAssignment from '../../components/RouteAssignment/RouteAssignment';
import useWebSocket from '../../hooks/useWebSocket';
import { useAPI } from '../../hooks/useAPI';

interface DashboardData {
  trackers: any[];
  assignments: any[];
  raceProgress: any;
  systemHealth: any;
  mapData: any;
  activeRoute?: any;
}

const MainDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { socket, connected } = useWebSocket(process.env.REACT_APP_WS_URL || 'http://localhost:8000');
  const api = useAPI();
  const hasFetched = useRef(false);

  // Load initial dashboard data
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;

      const loadDashboardData = async () => {
        try {
          setLoading(true);
          const [
            dashboardResponse,
            assignmentsResponse,
            mapResponse,
            healthResponse
          ] = await Promise.all([
            api.get('/dashboard/'),
            api.get('/assignments/current'),
            api.get('/dashboard/map'),
            api.get('/dashboard/system-health')
          ]);

          console.log('MainDashboard: Dashboard API response:', {
            hasActiveRoute: !!dashboardResponse.data.data.activeRoute,
            activeRoute: dashboardResponse.data.data.activeRoute,
            trackersCount: dashboardResponse.data.data.trackers?.length || 0,
            firstTrackerTitle: dashboardResponse.data.data.trackers?.[0]?.title,
            firstTrackerRole: dashboardResponse.data.data.trackers?.[0]?.role,
            responseKeys: Object.keys(dashboardResponse.data.data)
          });

          // CRITICAL DEBUG: Check exact data extraction
          console.log('MainDashboard: EXTRACTED activeRoute:', {
            extractedValue: dashboardResponse.data.data.activeRoute,
            typeOfValue: typeof dashboardResponse.data.data.activeRoute,
            isNull: dashboardResponse.data.data.activeRoute === null,
            isUndefined: dashboardResponse.data.data.activeRoute === undefined,
            stringified: JSON.stringify(dashboardResponse.data.data.activeRoute)
          });

          const newDashboardData = {
            trackers: dashboardResponse.data.data.trackers || [],
            assignments: assignmentsResponse.data.assignments || [],
            raceProgress: dashboardResponse.data.data.raceProgress || {},
            systemHealth: healthResponse.data || {},
            mapData: mapResponse.data || {},
            activeRoute: dashboardResponse.data.data.activeRoute
          };

          console.log('MainDashboard: SETTING state with activeRoute:', {
            settingActiveRoute: newDashboardData.activeRoute,
            typeOfActiveRoute: typeof newDashboardData.activeRoute,
            fullDashboardData: newDashboardData
          });

          setDashboardData(newDashboardData);
          setError(null);
        } catch (err: any) {
          console.error('Error loading dashboard data:', err);
          setError('Failed to load dashboard data. Please check your connection.');
        } finally {
          setLoading(false);
        }
      };

      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time WebSocket updates
  useEffect(() => {
    if (!socket) return;

    const handleDashboardUpdate = (data: any) => {
      console.log('MainDashboard: WebSocket dashboard update received:', {
        trackersCount: data.trackers?.length || 0,
        firstTrackerTitle: data.trackers?.[0]?.title,
        firstTrackerRole: data.trackers?.[0]?.role,
        hasActiveRouteInData: !!data.activeRoute,
        activeRouteFromWS: data.activeRoute
      });

      setDashboardData(prev => prev ? {
        ...prev,
        trackers: data.trackers || prev.trackers,  // Use fresh dashboard data with correct titles
        raceProgress: data.raceProgress || prev.raceProgress,
        systemHealth: data.systemHealth || prev.systemHealth,
        activeRoute: data.activeRoute || prev.activeRoute  // PRESERVE activeRoute!
      } : null);
    };

    const handleAssignmentChange = (data: any) => {
      console.log('MainDashboard: Assignment change received:', data);

      setDashboardData(prev => {
        if (!prev) return null;

        // Update both assignments and tracker data to reflect title changes
        const updatedTrackers = prev.trackers.map(tracker => {
          const updatedAssignment = data.assignments?.find((a: any) => a.trackerId === tracker.trackerId);
          if (updatedAssignment) {
            return {
              ...tracker,
              currentRole: updatedAssignment.role,
              title: updatedAssignment.title  // Fix: use 'title' field not 'customTitle'
            };
          }
          return tracker;
        });

        return {
          ...prev,
          assignments: data.assignments || prev.assignments,
          trackers: updatedTrackers
        };
      });
    };

    const handleSystemAlert = (alert: any) => {
      console.log('System alert:', alert);
      // Handle system alerts (could show snackbar notifications)
    };

    socket.on('dashboard:update', handleDashboardUpdate);
    socket.on('assignment:change', handleAssignmentChange);
    socket.on('system:alert', handleSystemAlert);

    return () => {
      socket.off('dashboard:update', handleDashboardUpdate);
      socket.off('assignment:change', handleAssignmentChange);
      socket.off('system:alert', handleSystemAlert);
    };
  }, [socket]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 2 }}>
        <Typography variant="h6" gutterBottom>Loading TV Production Dashboard...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top App Bar */}
      <AppBar position="static" sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid #333' }}>
        <Toolbar>
          <GpsFixed sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            TSRData RouteTracker - TV Production Dashboard
          </Typography>

          {/* Connection Status */}
          <Chip
            icon={<SignalCellularAlt />}
            label={connected ? 'Connected' : 'Disconnected'}
            color={connected ? 'success' : 'error'}
            variant="outlined"
            sx={{ mr: 2 }}
          />

          {/* Race Progress */}
          {dashboardData?.raceProgress && (
            <>
              <Chip
                icon={<Timeline />}
                label={`${dashboardData.raceProgress.fromStart?.kmDone?.toFixed(1) || 0}km / ${dashboardData.raceProgress.totalDistance?.toFixed(1) || 0}km`}
                color="primary"
                variant="outlined"
                sx={{ mr: 2 }}
              />

              {/* ETA Display */}
              {dashboardData.raceProgress.eta?.isValid && (
                <Chip
                  icon={<AccessTime />}
                  label={`ETA: ${dashboardData.raceProgress.eta.timeRemainingMinutes}min (${new Date(dashboardData.raceProgress.eta.estimatedFinishTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
                  color="error"
                  variant="outlined"
                  sx={{ mr: 2 }}
                />
              )}
            </>
          )}

          {/* Navigation Buttons */}
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<Dashboard />}
              onClick={() => navigate('/dashboard')}
              sx={{ color: 'white' }}
            >
              Dashboard
            </Button>
            <Button
              startIcon={<Route />}
              onClick={() => navigate('/routes')}
              sx={{ color: 'white' }}
            >
              Routes
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Main Dashboard Content */}
      <Box sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
          {/* Left Column - Map and Controls */}
          <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, height: '70vh' }}>
              {/* Live GPS Map */}
              <Box sx={{ flex: 2 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardHeader
                    title="Live GPS Tracking"
                    avatar={<GpsFixed />}
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 1 }}>
                    {(() => {
                      console.log('MainDashboard: BEFORE passing to LiveGPSMap:', {
                        hasActiveRoute: !!dashboardData?.activeRoute,
                        activeRoute: dashboardData?.activeRoute,
                        dashboardDataKeys: dashboardData ? Object.keys(dashboardData) : 'null',
                        typeOfActiveRoute: typeof dashboardData?.activeRoute,
                        fullDashboardData: dashboardData
                      });
                      return (
                        <LiveGPSMap
                          trackers={dashboardData?.trackers || []}
                          mapData={dashboardData?.mapData}
                          activeRoute={dashboardData?.activeRoute}
                          height="100%"
                        />
                      );
                    })()}
                  </CardContent>
                </Card>
              </Box>

              {/* Assignment Panel */}
              <Box sx={{ flex: 1 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardHeader
                    title="Tracker Assignment"
                    avatar={<Settings />}
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
                    <AssignmentPanel
                      trackers={dashboardData?.trackers || []}
                      assignments={dashboardData?.assignments || []}
                    />
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Control Panel and Route Assignment */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Production Controls */}
              <Box sx={{ flex: 1 }}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    title="Production Controls"
                    avatar={<Speed />}
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ p: 1 }}>
                    <ControlPanel
                      trackers={dashboardData?.trackers || []}
                    />
                  </CardContent>
                </Card>
              </Box>

              {/* Route Assignment */}
              <Box sx={{ flex: 1 }}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    title="Race Route Assignment"
                    avatar={<Route />}
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ p: 1, overflow: 'auto' }}>
                    <RouteAssignment
                      onRouteAssigned={(raceId) => {
                        // Handle route assignment
                        console.log('Route assigned:', raceId);
                      }}
                    />
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>

          {/* Right Column - Metrics and System Status */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Metrics Dashboard */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  title="Race Metrics"
                  avatar={<Terrain />}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
                  <MetricsDashboard
                    trackers={dashboardData?.trackers || []}
                    raceProgress={dashboardData?.raceProgress}
                    api={api}
                  />
                </CardContent>
              </Card>
            </Box>

            {/* System Health */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  title="System Health"
                  avatar={<SignalCellularAlt />}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
                  <SystemHealth
                    systemHealth={dashboardData?.systemHealth}
                    trackers={dashboardData?.trackers || []}
                  />
                </CardContent>
                </Card>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainDashboard;